/**
 * The Files tab's URL vocabulary (STD-029 R-10, arc B-5).
 *
 * One place spells `/files`. Everything that builds or reads a folder address
 * goes through here, which is what makes the scheme changeable: if vite's `base`
 * ever stops being `/`, or the display slug is switched off, this file is the
 * edit.
 *
 * **The id is the address and the slug is decoration.** `parseFolderRoute`
 * ignores segment 4 completely — it is generated from the folder's name purely
 * so a pasted link reads like something, and it is re-derived from the listing
 * on every load, so a rename cannot leave a link pointing at a stale word. A
 * link survives rename and move because the id survives both (M-7); a
 * path-shaped URL would not, and could not name a folder another organization
 * shared at all.
 *
 * Pure and free of `window`, so every rule here is unit-testable in the
 * node-environment vitest suite — this app ships no jsdom/RTL, so a rule left
 * inside a component or a hook has no tier that can hold it.
 */

/** The caller's own root has exactly ONE url, and this is it. */
export const FILES_ROOT_PATH = '/files';

/** Longest slug we will emit. A URL is for reading, not for carrying a name. */
const MAX_SLUG_LENGTH = 60;

/**
 * Letters that carry no NFKD decomposition, so stripping combining marks cannot
 * reach them. Without this map `Ø` and `Æ` fall through to the non-alphanumeric
 * branch and vanish — a Scandinavian folder name would slug to something that
 * reads like a different folder. Deliberately short: it covers the latin letters
 * a European fleet actually names folders with, and anything else is allowed to
 * disappear, because the slug is decoration and the id is the address.
 */
const UNDECOMPOSABLE: Record<string, string> = {
  ø: 'o',
  æ: 'ae',
  œ: 'oe',
  ß: 'ss',
  đ: 'd',
  ð: 'd',
  þ: 'th',
  ł: 'l',
};

/**
 * Is this string shaped like a `fleet.file_objects` row id?
 *
 * Checked on the CLIENT before any request, and the reason is the sentence the
 * user reads rather than the round trip saved: the endpoint types its path
 * param as `uuid.UUID`, so a mistyped segment comes back 422 — which the failure
 * classifier would then report as "that link points at a file, not a folder".
 * Wrong sentence for a broken link.
 *
 * Shape only (8-4-4-4-12 hex), not version/variant: Postgres accepts any uuid,
 * and refusing a legal id because it was minted by something other than v4
 * would be this function inventing a rule the database does not have.
 */
const OBJECT_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isObjectId(value: string): boolean {
  return OBJECT_ID_RE.test(value);
}

/**
 * A folder name reduced to url decoration. May legitimately return `''` — a
 * name with no latin alphanumerics (`日本語`) has no slug, and the caller must
 * then omit the segment entirely rather than emit a trailing slash.
 */
export function folderSlug(name: string): string {
  const folded = Array.from(name.toLowerCase())
    .map((character) => UNDECOMPOSABLE[character] ?? character)
    .join('')
    .normalize('NFKD')
    // Combining marks left behind by the decomposition (´ ¨ ˚ …).
    .replace(/[\u0300-\u036f]/g, '');

  return (
    folded
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, MAX_SLUG_LENGTH)
      // A second trim: the truncation above can land mid-separator.
      .replace(/-+$/g, '')
  );
}

/**
 * The url for one folder. `null` is the caller's own organization root, which
 * has one spelling and it is not `/files/folder/<root id>` — see
 * `parseFolderRoute`'s note on why that address still WORKS.
 */
export function folderUrl(id: string | null, name?: string): string {
  if (id === null) return FILES_ROOT_PATH;
  const slug = name ? folderSlug(name) : '';
  return slug ? `${FILES_ROOT_PATH}/folder/${id}/${slug}` : `${FILES_ROOT_PATH}/folder/${id}`;
}

export type FolderRoute =
  /** Not a Files url at all — the other tabs, `/guide`, `/manufacturing`, … */
  | { kind: 'none' }
  /** `/files` — the caller's own root. */
  | { kind: 'root' }
  /** `/files/folder/<uuid>[/<ignored slug>]`. */
  | { kind: 'folder'; objectId: string }
  /** A Files url naming something that cannot be a row id. */
  | { kind: 'invalid' };

/**
 * Read a pathname as a Files route.
 *
 * **Segment 4 is never read.** That is the property the whole slug scheme rests
 * on, and it is what makes the slug free to change, to be wrong after a rename,
 * or to be dropped later with no compatibility cost.
 *
 * `/files/folder/<the caller's own root id>` parses as a normal folder and works
 * — the server states that `/root/children` and `/{that id}/children` name one
 * folder. The browser normalizes the URL to `/files` once the listing comes back
 * and says the node has no parent, but it normalizes the *url*, not the node:
 * navigation state keeps the real root id, because `{id: null}` aliases the root
 * and that alias is what the clipboard-origin bug needed.
 */
export function parseFolderRoute(pathname: string): FolderRoute {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'files') return { kind: 'none' };
  if (segments.length === 1) return { kind: 'root' };
  if (segments[1] !== 'folder' || segments.length < 3) return { kind: 'invalid' };
  return isObjectId(segments[2]) ? { kind: 'folder', objectId: segments[2] } : { kind: 'invalid' };
}
